import type { ResolutionLadder } from "../../../utils/timeSeriesTiles";

/**
 * JSON-safe recursive types.
 *
 * `source.ref` is serialized to storage — a BigInt, function, or cyclic
 * reference there would throw at persist time, far from the block that caused
 * it. These types enforce JSON-safety at the type level.
 */
type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
type JsonObject = { [key: string]: JsonValue };

/**
 * WHERE a chart's data comes from.
 *
 * ── Why this union exists ─────────────────────────────────────────────────────
 * A chart either holds its data or streams it. Until now those were two
 * different COMPONENTS — `ChartContainer` for the first, `TiledTimeSeriesChart`
 * for the second — which meant the choice was made when you picked an import,
 * and a block (whose props must be JSON) could only ever reach the first.
 *
 * Loading strategy is not a kind of chart. It is a property OF one. So it moves
 * into a prop, and the two cases become one component with two sources.
 *
 * Modelled on `MapDataSource` in `@texturehq/edges-blocks-maps`, which made the
 * same call for geometry: `{ kind: "inline" } | { kind: "tileset" }`, with the
 * fetching itself left to the runtime. One idea, two media.
 *
 * ── The serialization rule ────────────────────────────────────────────────────
 * Everything here is JSON-safe on purpose, because that is what lets a BLOCK
 * declare it: block props are stringified into `data-composition-schema`, and a
 * function there blanks the page. So this union says WHICH series to draw and at
 * what resolutions — never HOW to fetch them. The fetcher is supplied separately
 * at the render site (see `ChartSourceResolver`), exactly as the maps blocks
 * declare a tileset and let the host attach `createAuthenticatedTileTransform`.
 */
export type ChartSource = InlineChartSource | StreamingChartSource;

/**
 * Data the caller already has. The default, and what every chart does today —
 * series children carry their own `data` arrays.
 *
 * Declared explicitly (rather than inferred from the absence of a source) so a
 * composition can say "inline" out loud, and so a schema can reject a block that
 * sets both `series` and a streaming source.
 */
export interface InlineChartSource {
  kind: "inline";
}

/**
 * Data fetched as the reader moves through it.
 *
 * The chart requests slices as the camera pans and re-requests at a coarser or
 * finer resolution as it zooms. Which slices, and how they are cached, is the
 * renderer's business — a caller describes the SHAPE of the history and nothing
 * else.
 *
 * `ref` is the part a host resolves. It is deliberately opaque here: edges
 * cannot know what a "meter demand series" is, and should not learn. The app
 * maps it to a real fetcher (see `ChartSourceResolver`), which is what keeps
 * this package free of GraphQL and any app dependency.
 */
export interface StreamingChartSource {
  kind: "streaming";
  /**
   * Opaque, serializable descriptor of WHICH series to stream — resolved by the
   * host. In the dashboard this is a `Binding`; edges never inspects it.
   */
  ref: JsonObject;
  /**
   * Resolutions this history can be drawn at, coarsest to finest, with the span
   * of one fetched slice at each. Plain data — `[{ id: "1h", bucketMs, tileSpanMs }]`.
   */
  ladder: ResolutionLadder;
  /**
   * The finest spacing the underlying data actually has, in ms.
   *
   * Optional because it is frequently NOT known up front: a source that probes
   * its own cadence discovers this after its first response, and the renderer
   * already handles it arriving late. Pass it when the value is known
   * statically; otherwise let the resolver report it.
   */
  nativeMs?: number;
  /** The window to open on. Defaults to the last 30 days. */
  initialRange?: { days: number };
  /**
   * The instant this chart is pinned to, as an ISO-8601 string.
   *
   * ── What it is ────────────────────────────────────────────────────────────
   * Time travel. A surface showing a historical moment — Explore's scrubber,
   * a match event under DER detection — pins every panel to the same instant,
   * and this is how a streaming chart joins that. `initialRange` says how WIDE
   * the opening window is; this says where its right edge sits. Panning and
   * zooming stay unrestricted from there.
   *
   * ── 🔴 An anchored chart must not go live ─────────────────────────────────
   * The two are mutually exclusive, and the failure is the quiet kind: a chart
   * that kept refreshing while pinned would advance to now while the rest of
   * the panel stayed in the past, so the page would show two different
   * instants and look entirely normal doing it. The renderer already declines
   * to follow when this is set (`canFollow`, `atLiveEdge`), and a host that
   * offers a live control is expected to withhold it for the same reason.
   *
   * A STRING rather than a `Date`, because this declaration is stored: a board
   * tile or a composition carries it as JSON, and `Date` does not survive that
   * round trip. The renderer parses it once.
   */
  viewportEnd?: string;
  /**
   * Changing this discards fetched data and starts over — use it when the
   * IDENTITY of what is plotted changes (a different device, a different
   * workspace). Panning and zooming do not need it.
   */
  resetKey?: string;
  /**
   * Changing this re-validates what is on screen WITHOUT clearing the plot, so
   * a refresh does not blank the chart. Use it for live updates.
   */
  refreshKey?: string;
}

/**
 * The runtime half of a streaming source: turns the serializable `ref` into
 * something that can actually fetch.
 *
 * This is a FUNCTION and therefore never a block prop. It is supplied once at
 * the render site — a provider near the top of the app — so that every chart
 * below it can stream without any of them importing a data client. Mirrors how
 * the maps blocks declare a tileset in props while the host attaches tile
 * authentication at the `<Map>` itself.
 *
 * Returning `null` means "I do not know this ref" — the chart then renders its
 * empty state rather than throwing, which is the right behaviour for a board
 * tile whose source is no longer registered.
 */
export type ChartSourceResolver = (source: StreamingChartSource) => ResolvedChartSource | null;

/**
 * What a resolver hands back: the fetcher, plus anything it learned about the
 * data that the declaration could not state up front.
 */
export interface ResolvedChartSource {
  /**
   * Fetches one slice. The renderer owns when and how often this is called.
   *
   * Typed structurally rather than as `FetchTile` so this file stays free of
   * the tile vocabulary in its public surface; it is the same function.
   */
  fetchSlice: import("../../../utils/timeSeriesTiles").FetchTile;
  /**
   * The finest real spacing of the data, if the resolver discovered it.
   *
   * 🔴 This is an OUTPUT, not an input, and that is load-bearing. A source that
   * probes its own cadence only learns it after a response, and the renderer
   * deliberately re-picks its resolution when this arrives late — without that,
   * a chart stays pinned to the resolution its fallback guessed. Overrides
   * `StreamingChartSource.nativeMs` when both are present.
   */
  nativeMs?: number;
  /**
   * How many backend series one slice costs, for the renderer's fetch budget.
   * Defaults to the renderer's own default when omitted.
   */
  costSeriesCount?: number;
  /**
   * How many underlying REQUESTS one `fetchSlice` call issues. Defaults to 1.
   *
   * 🔴 Distinct from `costSeriesCount`, and the two answer different questions.
   * That one is about volume — how much data a tier costs — and feeds the
   * overscan guard inside a single chart's ladder. This one is about
   * CONCURRENCY: how many requests hit the wire at once, which is what the
   * page-wide ceiling counts.
   *
   * They come apart wherever a resolver merges several loaders into one slice.
   * The grid cohort's role-split slice fetches net, consumption and generation
   * — three requests, charged one permit unless it says so, and twelve such
   * slices would then run 36 requests under a ceiling reporting 12.
   *
   * Only a resolver that fans out needs to set it; a slice that makes one
   * request should leave it alone.
   */
  fetchesPerSlice?: number;

  /*
   * ── The live half of the contract ─────────────────────────────────────────
   *
   * 🔴 These are FUNCTIONS and stateful flags, and they live HERE rather than
   * in the declaration for a reason that is easy to get backwards.
   *
   * A chart that updates itself is a conversation, not a description: the
   * reader moves the camera, the host learns where they are looking, decides
   * whether that position can be kept live, and answers by refreshing. A block
   * cannot hold either half of that — a callback is not JSON, and the answer
   * depends on a subscription the block has no business owning.
   *
   * But a HOST already can. `ChartSourceHost` is a component (M2's amendment),
   * so it may hold hooks, and `TiledTimeSeriesView` is plain data. So the
   * signal goes up as JSON and the behaviour comes back down from the host,
   * and the block in the middle stays entirely declarative. That is what lets
   * a streaming block reach full parity with a hand-built island without
   * growing a single function-shaped prop.
   */

  /**
   * The Live/Static affordance, when this source can serve live updates.
   *
   * Omit it and no control renders at all — which is the correct reading of a
   * meter that is not in the live pilot, rather than a disabled button whose
   * reason nobody can act on.
   */
  liveControl?: import("../TiledTimeSeriesExplorer").TiledTimeSeriesLiveControl;
  /**
   * The reader's committed camera position, reported as it changes.
   *
   * This is the upward half of the loop: a host discovering the meter's real
   * cadence needs to know which window is on screen and whether it is pinned to
   * the live edge, because the answer decides what it may probe for and whether
   * a refresh is even meaningful.
   */
  onViewChange?: (view: import("../TiledTimeSeriesExplorer").TiledTimeSeriesView) => void;
  /** Keep an unanchored view advancing at Now. */
  followLatest?: boolean;
  /** Change to resume a navigation-paused view at Now, preserving its span. */
  followLatestKey?: string | number;
  /**
   * Whether automatic refreshes may run at all.
   *
   * 🔴 Defaults to TRUE when omitted, which is why a host that offers a
   * Live/Static control MUST set it. "Static" that still refreshes in the
   * background is the quiet kind of wrong: the button reads paused, the data
   * keeps moving, and nothing errors.
   */
  refreshEnabled?: boolean;
  /**
   * Re-validate what is on screen without clearing the plot.
   *
   * Overrides `StreamingChartSource.refreshKey` when present, because a live
   * host derives this from a delivered signal rather than from the declaration.
   */
  refreshKey?: string;
}

/**
 * The same job as `ChartSourceResolver`, for sources that cannot be a function.
 *
 * ── Why this exists alongside the resolver ────────────────────────────────────
 * A resolver is called from inside a `useMemo`, which means it may not call
 * hooks. Most sources do not care. But a source that PROBES ITS OWN CADENCE
 * does: discovering the finest real spacing of a meter's data takes a query,
 * that query's result arrives after the first render, and `nativeMs` therefore
 * has to be able to change over the life of the chart. In the dashboard that
 * logic is already a hook (`useMeterUsageTileSource`) and is several hundred
 * lines of Apollo queries, abort handling and settle-state — none of which can
 * be flattened into a synchronous call, and none of which should be rewritten
 * to satisfy a type.
 *
 * So a host is a COMPONENT rather than a function. It renders, which is exactly
 * what buys it the right to hold hooks, and it hands back what it resolved
 * through a child function. The chart does not care which half of the contract
 * answered it.
 *
 * 🔴 This is a COMPONENT TYPE. `StreamingChart` renders it as `<Host …>`, and
 * that is load-bearing rather than stylistic: a host invoked as a plain function
 * would have its hooks counted against the CALLER's hook list, and since a host
 * is optional that list would change length whenever one is installed or
 * removed. Holding hooks is the whole job here, so the host must own a fiber.
 * Do not "simplify" the render back into a call.
 *
 * 🔴 The two "no answer yet" cases are signalled DIFFERENTLY, and conflating
 * them is a real bug:
 *   - **still resolving** → render nothing (return `null`) WITHOUT calling
 *     `children`. The chart keeps its own loading state.
 *   - **not my ref** → call `children(null)`. The chart falls through to any
 *     plain resolver installed alongside, then to its empty state.
 *
 * A probing source that used `children(null)` for "still resolving" would flash
 * "No data source" for the length of its first query before drawing.
 */
export type ChartSourceHost = (props: {
  source: StreamingChartSource;
  children: (resolved: ResolvedChartSource | null) => import("react").ReactNode;
}) => import("react").ReactNode;

/** Narrowing helper — a source that streams rather than carrying its data. */
export function isStreamingSource(source: ChartSource | undefined): source is StreamingChartSource {
  return source?.kind === "streaming";
}

/**
 * One ISO-8601 instant, or `null`.
 *
 * ── 🔴 Why `Date.parse` alone is not enough ───────────────────────────────────
 * Three of its behaviours are wrong for a STORED anchor, and all three fail
 * quietly:
 *
 *   - **A missing timezone is read as the VIEWER's.** `"2026-09-01T12:00:00"`
 *     parses to a different real instant in every timezone, so one saved
 *     composition anchors two readers to two different moments — and each sees
 *     a chart that looks entirely correct.
 *   - **Impossible dates are normalised.** `"2026-02-30"` silently becomes
 *     March 2 rather than failing.
 *   - **Non-ISO strings are accepted.** `"March 1 2026"` parses fine, so a
 *     declaration can drift from the format the contract promises.
 *
 * An absolute instant has to be unambiguous, so this requires an explicit `Z`
 * or numeric offset and rejects anything the calendar does not contain. Shared
 * by the renderer, the block schema and the host so all three agree on what
 * counts as an anchor — a value one accepted and another rejected would pin the
 * chart while leaving live mode on, which is the worst of both.
 */
export function parseInstant(value: unknown): Date | null {
  if (typeof value !== "string") return null;
  // Date-time with seconds, optional milliseconds, and a REQUIRED zone.
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!match) return null;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  const parsed = new Date(ms);
  /*
   * Round-trip the calendar fields to reject what `Date` would normalise.
   * February 30 parses and becomes March 2; comparing the UTC month and day
   * back against the string catches that, using the offset the string itself
   * declared rather than the viewer's.
   */
  const [, year, month, day] = match;
  const utc = new Date(`${year}-${month}-${day}T00:00:00Z`);
  if (utc.getUTCFullYear() !== Number(year) || utc.getUTCMonth() + 1 !== Number(month)) return null;
  if (utc.getUTCDate() !== Number(day)) return null;
  return parsed;
}
